import { inject } from "@angular/core";
import { signalStore, withState, withMethods, patchState, withHooks } from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { pipe, switchMap, tap } from "rxjs";
import { SettingsService } from "../infra/settings.service";
import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from "@application/data-access";
import { TSystemCultures, TOwnerSettings } from "../entities/settings.model";
import { ErrorStore } from "@application/errors/data-access";
import { IFragment } from "@shared/components/ui/summary";
import { tapResponse } from "@ngrx/operators";
import { HttpErrorResponse } from '@angular/common/http';

export interface ISettings {
  phoneNumberCountryCode: any;
  systemTimeZones: Record<string, string>;
  systemCultures: TSystemCultures[];
  ownerSettings: TOwnerSettings;
  pageFragments: IFragment[];
}

export type ISystemCulture = {
  id: string;
  value: string;
};

export const SettingsInitialState: ISettings = {
  phoneNumberCountryCode: null,
  systemTimeZones: null,
  systemCultures: null,
  ownerSettings: null,
  pageFragments: null
};

export const SettingsStore = signalStore(
  { providedIn: 'root' },
  withState(SettingsInitialState),
  withMethods((
    store,
    settingsService = inject(SettingsService), errorStore = inject(ErrorStore)) => {

    return {

      loadOwnerSettings: rxMethod<void>(pipe(
        tap(() => patchState(store, setPropLoading('loadOwnerSettings'))),
        switchMap(() => settingsService.getOwnerSettings().pipe(
          tapResponse({
            next: (ownerSettings) => {
              patchState(store, { ownerSettings, ...setPropLoaded('loadOwnerSettings') })
            },
            error: (error) => {
              // errorStore.setHttpError(error);
              patchState(store, setPropError('loadOwnerSettings', error));
            }
          })
        )))),
      loadSystemCultures: rxMethod<void>(pipe(
        tap(() => patchState(store, setPropLoading('loadSystemCultures'))),
        switchMap(() => settingsService.getSystemCultures().pipe(
          tapResponse({
            next: (response) => patchState(store, { systemCultures: response, ...setPropLoaded('loadSystemCultures') }),
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError('loadSystemCultures', error));
            }
          })
        ))
      )),
      updateOwnerSettings: rxMethod<Partial<TOwnerSettings>>(pipe(
        tap(() => patchState(store, setPropLoading('updateOwnerSettings'))),
        switchMap((payload) => settingsService.updateOwnerSettings(payload).pipe(
          tapResponse({
            next: (ownerSettings) => patchState(store, { ownerSettings }, setPropLoaded('updateOwnerSettings')),
            error: (error: HttpErrorResponse) => {
              patchState(store, setPropError('updateOwnerSettings', error));
            }
          })
        ))
      )),
      updateOwnerSettingsGeneral: rxMethod<Partial<TOwnerSettings>>(pipe(
        tap(() => patchState(store, setPropLoading('updateOwnerSettings'))),
        switchMap((payload) => settingsService.updateOwnerSettingsGeneral(payload).pipe(
          tapResponse({
            next: (ownerSettings) => patchState(store, { ownerSettings }, setPropLoaded('updateOwnerSettings')),
            error: (error: HttpErrorResponse) => {
              patchState(store, setPropError('updateOwnerSettings', error));
            }
          })
        ))
      )),
      loadSystemTimeZones: rxMethod<void>(pipe(
        tap(() => patchState(store, setPropLoading('loadSystemTimeZones'))),
        switchMap(() => settingsService.getSystemTimeZones().pipe(
          tapResponse({
            next: (response) => patchState(store, { systemTimeZones: response, ...setPropLoaded('loadSystemTimeZones') }),
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError('loadSystemTimeZones', error));
            }
          })
        ))
      )),
      setPageFragments: (fragments: IFragment[]) => {
        patchState(store, { pageFragments: fragments });
      },
      resetReqState: () => {
        patchState(store, setPropInit('loadSystemTimeZones', 'loadOwnerSettings', 'loadSystemCultures', 'updateOwnerSettings'));
      }
    }
  }),
  withReqState('loadSystemTimeZones', 'loadOwnerSettings', 'loadSystemCultures', 'updateOwnerSettings'),
  withHooks({
    onInit: (store) => {
      store.ownerSettings();
    }
  })
);
